// The starting cost model of a sea route to the Philippines (14 §14.7): the owner's October 2026
// estimates for a 40 ft high cube, split into the bills that really arrive, each with the rule that
// dates it on the cash calendar and the category that decides its reserve bucket and when it is
// accrued (06 §6.3). Pure data; the reference seed and the system test seed both start from it.
//
//   Freight and export BL  AED 15,000  → ocean freight, BL and export documents, telex release
//   Customs clearance      PHP 180,000 → port charges and arrastre, delivery order, duties, brokerage
//   Delivery               PHP 200,000 → pull-out trucking, devanning, last mile own and partner
import type { CostLine } from './movement-costs';

export interface StartingCostLine extends CostLine {
  readonly label: string;
  readonly dueRule: string;
}

/** The lines of the starting model, in the order they fall due. */
export const STARTING_COST_LINES: readonly StartingCostLine[] = [
  // ---- origin: paid by the country that sold the boxes (freight and telex reserves)
  {
    item: 'ocean_freight',
    label: 'Ocean freight (shipping line)',
    amountMinor: 1_350_000,
    currency: 'AED',
    payer: 'origin',
    dueRule: 'ETD+30',
    category: 'freight',
  },
  {
    item: 'bl_export_docs',
    label: 'BL and export documents',
    amountMinor: 75_000,
    currency: 'AED',
    payer: 'origin',
    dueRule: 'ETD+0',
    category: 'telex_bl',
  },
  {
    item: 'telex_release',
    label: 'Telex release',
    amountMinor: 75_000,
    currency: 'AED',
    payer: 'origin',
    dueRule: 'ETA-5',
    category: 'telex_bl',
  },
  // ---- destination, at the port: due on arrival, accrued when customs releases the container
  {
    item: 'port_arrastre',
    label: 'Port charges and arrastre',
    amountMinor: 2_500_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'ATA',
    category: 'port_storage',
  },
  {
    item: 'delivery_order',
    label: 'Delivery order (DO) fee',
    amountMinor: 1_000_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'ATA',
    category: 'port_storage',
  },
  {
    item: 'customs_duties',
    label: 'Customs duties and taxes',
    amountMinor: 12_500_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'ATA',
    category: 'customs',
  },
  {
    item: 'brokerage',
    label: 'Customs brokerage',
    amountMinor: 2_000_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'ATA',
    category: 'customs',
  },
  // ---- destination, port to hub: accrued at pull-out
  {
    item: 'trucking_pullout',
    label: 'Trucking, port to hub (pull-out)',
    amountMinor: 2_500_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'PULLOUT',
    category: 'trucking',
  },
  {
    item: 'devanning',
    label: 'Devanning at the hub',
    amountMinor: 1_000_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'DEVAN',
    category: 'trucking',
  },
  // ---- destination, the last mile: per box, by the boxes carried (or the per-box table)
  {
    item: 'last_mile_own',
    label: 'Last mile, own delivery runs (per box)',
    amountMinor: 10_500_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'DEVAN+7',
    category: 'delivery',
  },
  {
    item: 'last_mile_partner',
    label: 'Last mile, partner forwarding (per box)',
    amountMinor: 6_000_000,
    currency: 'PHP',
    payer: 'destination',
    dueRule: 'HANDOVER+7',
    category: 'forwarding',
  },
];

/** A fresh copy of the starting lines, for a seed to write. */
export function startingCostLines(): StartingCostLine[] {
  return STARTING_COST_LINES.map((l) => ({ ...l }));
}

// ---- air and loose cargo (2026-10-30): charged by weight and volume, not by the container. Each
// line is the cost of the planned load (30 air boxes, 60 LCL boxes), and a container pays its share
// by the boxes it carries (`chargedByLoad`). SAMPLE figures: the owner replaces them.

const line = (
  item: string,
  label: string,
  amountMinor: number,
  currency: 'AED' | 'PHP',
  payer: 'origin' | 'destination',
  dueRule: string,
  category: string,
): StartingCostLine => ({ item, label, amountMinor, currency, payer, dueRule, category });

/** An air consolidation of about 30 boxes (one air waybill). */
export const AIR_STARTING_COST_LINES: readonly StartingCostLine[] = [
  line(
    'air_freight',
    'Air freight (airline, by chargeable kg)',
    900_000,
    'AED',
    'origin',
    'ETD+7',
    'freight',
  ),
  line(
    'awb_docs',
    'Air waybill and export documents',
    15_000,
    'AED',
    'origin',
    'ETD+0',
    'telex_bl',
  ),
  line(
    'airport_handling',
    'Airport terminal handling',
    600_000,
    'PHP',
    'destination',
    'ATA',
    'port_storage',
  ),
  line(
    'customs_duties',
    'Customs duties and taxes',
    1_800_000,
    'PHP',
    'destination',
    'ATA',
    'customs',
  ),
  line('brokerage', 'Customs brokerage', 400_000, 'PHP', 'destination', 'ATA', 'customs'),
  line(
    'trucking_pullout',
    'Trucking, airport to hub',
    300_000,
    'PHP',
    'destination',
    'PULLOUT',
    'trucking',
  ),
  line(
    'last_mile_own',
    'Last mile, own delivery runs (per box)',
    1_050_000,
    'PHP',
    'destination',
    'DEVAN+7',
    'delivery',
  ),
  line(
    'last_mile_partner',
    'Last mile, partner forwarding (per box)',
    600_000,
    'PHP',
    'destination',
    'HANDOVER+7',
    'forwarding',
  ),
];

/** A share of a sea container (LCL) of about 60 boxes, through a consolidator's warehouse (CFS). */
export const LCL_STARTING_COST_LINES: readonly StartingCostLine[] = [
  line(
    'ocean_freight',
    'Ocean freight, LCL (by cubic metre)',
    360_000,
    'AED',
    'origin',
    'ETD+30',
    'freight',
  ),
  line(
    'bl_export_docs',
    'House BL and export documents',
    30_000,
    'AED',
    'origin',
    'ETD+0',
    'telex_bl',
  ),
  line('telex_release', 'Telex release', 15_000, 'AED', 'origin', 'ETA-5', 'telex_bl'),
  line(
    'cfs_charges',
    'Consolidator warehouse (CFS) and port charges',
    900_000,
    'PHP',
    'destination',
    'ATA',
    'port_storage',
  ),
  line(
    'delivery_order',
    'Delivery order (DO) fee',
    300_000,
    'PHP',
    'destination',
    'ATA',
    'port_storage',
  ),
  line(
    'customs_duties',
    'Customs duties and taxes',
    2_500_000,
    'PHP',
    'destination',
    'ATA',
    'customs',
  ),
  line('brokerage', 'Customs brokerage', 400_000, 'PHP', 'destination', 'ATA', 'customs'),
  line(
    'trucking_pullout',
    'Trucking, CFS to hub',
    600_000,
    'PHP',
    'destination',
    'PULLOUT',
    'trucking',
  ),
  line('devanning', 'Unloading at the hub', 300_000, 'PHP', 'destination', 'DEVAN', 'trucking'),
  line(
    'last_mile_own',
    'Last mile, own delivery runs (per box)',
    2_100_000,
    'PHP',
    'destination',
    'DEVAN+7',
    'delivery',
  ),
  line(
    'last_mile_partner',
    'Last mile, partner forwarding (per box)',
    1_200_000,
    'PHP',
    'destination',
    'HANDOVER+7',
    'forwarding',
  ),
];

/** A fresh copy of the starting lines for a container type: air, LCL, or a full sea container. */
export function startingCostLinesFor(containerType: string): StartingCostLine[] {
  const lines =
    containerType === 'air'
      ? AIR_STARTING_COST_LINES
      : containerType === 'lcl'
        ? LCL_STARTING_COST_LINES
        : STARTING_COST_LINES;
  return lines.map((l) => ({ ...l }));
}
